import React, {createContext, useEffect, useContext, useState} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import UserStateContext from './UserStateContext';
import SummaryContext from './SummaryContext';
import noop from '../utils/noop';

const Context = createContext({
  showTwoGiftsPrompt: false,
  isDeterminingTwoGiftsPrompt: false,
  dismissTwoGiftsPrompt: noop,
});

export default Context;

const shouldDisplayTwoGifts = (
  {totalActiveGiftsRequested},
  twoGiftsDismissed,
) => {
  const isNotEnoughItems = totalActiveGiftsRequested < 260;

  if (!isNotEnoughItems) return false;

  if (!twoGiftsDismissed && isNotEnoughItems) return true;

  const daysSinceDismissed = Math.floor(
    (new Date() - new Date(twoGiftsDismissed)) / 8.64e7, // number of milliseconds in 1 day
  );

  if (isNotEnoughItems && daysSinceDismissed > 60) return true;

  return false;
};

export const TwoGiftsContextProvider = ({children}) => {
  // Contexts
  const {userStateData} = useContext(UserStateContext);
  const {summary, isFetching: isFetchingSummary} = useContext(SummaryContext);

  // State
  const [shouldDisplay, setShouldDisplay] = useState(null);

  // Effects
  useEffect(() => {
    if (!userStateData && !isFetchingSummary) {
      return;
    }

    const determineShouldDisplay = async () => {
      const result = get(userStateData, 'twoGiftsDismissed', false);
      const passedDisplayCriteria = shouldDisplayTwoGifts(summary, result);

      setShouldDisplay(passedDisplayCriteria);
    };

    determineShouldDisplay();
  }, [userStateData, summary, isFetchingSummary]);

  // Locals
  const isDeterminingTwoGiftsPrompt =
    shouldDisplay === null || isFetchingSummary;

  return (
    <Context.Provider
      value={{
        showTwoGiftsPrompt: !isDeterminingTwoGiftsPrompt && shouldDisplay,
        isDeterminingTwoGiftsPrompt,
        dismissTwoGiftsPrompt: () => setShouldDisplay(false),
      }}>
      {children}
    </Context.Provider>
  );
};

TwoGiftsContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
